import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Checkbox, Flex, Separator, Text } from '@radix-ui/themes';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { smartHubApi } from '../../services/api';
import type {
  BillingGapsFeed,
  BulkAckGroup,
  SmartHubAnswerRow,
  SmartHubEhrChangeRow,
  SmartHubItem,
  SmartHubSubmissionGroup,
  SmartHubTaskRow,
  TaskBarFeed,
} from './types';
import { isEhrChange, isReading, isTask, itemKey } from './types';
import { PATIENT_DETAIL_TAB_INDEX } from '../PatientDetailTabIndex';
import {
  describeEhrChanges,
  fitSummaryLines,
  setEhrChangeFocus,
  type EhrChangeFocusSection,
  type EhrChangeSummaryLine,
} from '../../utils/ehrChanges';

// ---------------------------------------------------------------------------
// PHA-1141 / PHA-1142: an EHR change row names the changes themselves and opens the chart
// where each is reviewed, WITHOUT acknowledging on the way (the old click acknowledged the
// whole run before the clinician had seen a thing). The run clears once every item in it is
// decided in the chart. A row with no itemised changes keeps the old click-to-acknowledge.
// ---------------------------------------------------------------------------
const SECTION_TAB: Record<EhrChangeFocusSection, number> = {
  problems: PATIENT_DETAIL_TAB_INDEX.PROBLEMS,
  medications: PATIENT_DETAIL_TAB_INDEX.MEDICATIONS,
  vitals: PATIENT_DETAIL_TAB_INDEX.CARE_PLANS,
};

const observationTypesOf = (row: SmartHubEhrChangeRow): string[] => {
  const cs = row.change_summary || {};
  return Array.from(
    new Set([...(cs.vitals_added || []), ...(cs.labs_added || [])].map((o) => String(o.document_type || ''))),
  ).filter(Boolean);
};

export const openEhrChangeInChart = (row: SmartHubEhrChangeRow, section: EhrChangeFocusSection) => {
  setEhrChangeFocus(row.patient_id, {
    section,
    documentTypes: section === 'vitals' ? observationTypesOf(row) : undefined,
  });
  window.dispatchEvent(
    new CustomEvent('phanova:open-patient-detail', {
      detail: { patientId: row.patient_id, initialTab: SECTION_TAB[section] },
    }),
  );
};

/** Handled by Dashboard, which owns the onboarding tab and its unsaved-changes guard. */
const startEhrChangeRegeneration = (row: SmartHubEhrChangeRow) => {
  window.dispatchEvent(
    new CustomEvent('phanova:regenerate-care-plan', {
      detail: { patientId: row.patient_id, source: 'ehr_changes' },
    }),
  );
};

const ehrChangeLines = (row: SmartHubEhrChangeRow): EhrChangeSummaryLine[] =>
  describeEhrChanges(row.change_summary as any, row.started_at);

const firstSection = (lines: EhrChangeSummaryLine[]): EhrChangeFocusSection | null =>
  (['problems', 'medications', 'vitals'] as EhrChangeFocusSection[]).find((s) =>
    lines.some((l) => l.section === s),
  ) || null;

const formatRelative = (iso: string | null): string => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

// Renders the calendar day directly from the ISO 8601 string the backend
// emits for care-plan task release_at / due_at. Going through `new Date()`
// + `toLocaleDateString()` silently shifts UTC midnight back one day for
// users east of the source TZ — visible bug: a 2026-03-30 due date showing
// as "3/29/2026". Care-plan dates are clinic-calendar days, not moments,
// so render the date portion as written. Clinics operate on US Eastern;
// any future date math should anchor there explicitly.
export const formatTaskCalendarDay = (iso: string | null): string => {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const [, year, month, day] = m;
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
};

const itemSummary = (item: SmartHubItem): { primary: string; secondary: string } => {
  if (isEhrChange(item)) {
    // Same formatting as the dedicated EHR section so the row reads
    // identically when it appears in the flat To-do union below.
    return ehrChangeRowText(item);
  }
  if (isTask(item)) {
    const dupSuffix =
      item.duplicate_count && item.duplicate_count > 1
        ? ` · ×${item.duplicate_count} care plans`
        : '';
    return {
      primary: item.task,
      secondary:
        (item.is_overdue
          ? `Overdue · due ${formatTaskCalendarDay(item.due_at)}`
          : item.release_at
            ? `Due ${formatTaskCalendarDay(item.release_at)}`
            : 'Upcoming') + dupSuffix,
    };
  }
  if (isReading(item)) {
    let val: string;
    const v: any = item.value;
    if (v && typeof v === 'object' && 'systolic' in v && 'diastolic' in v) {
      val = `${v.systolic}/${v.diastolic}`;
    } else if (typeof v === 'number') {
      val = String(v);
    } else if (typeof v === 'string') {
      val = v;
    } else {
      val = '—';
    }
    return {
      primary: `${item.reading_type ?? 'Reading'}: ${val}`,
      secondary: formatRelative(item.submission_time),
    };
  }
  // Survey answer
  const a = item as Extract<SmartHubItem, { source_kind: 'survey' }>;
  let val: string;
  const v: any = a.value;
  if (v == null) val = '—';
  else if (typeof v === 'boolean') val = v ? 'Yes' : 'No';
  else if (typeof v === 'object') {
    if ('systolic' in v && 'diastolic' in v) val = `${v.systolic}/${v.diastolic}`;
    else val = JSON.stringify(v);
  } else val = String(v);
  return {
    primary: `${a.prompt.length > 60 ? a.prompt.slice(0, 57) + '…' : a.prompt}`,
    secondary: `${val} · ${formatRelative(a.submission_time)}`,
  };
};

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <Box
    style={{
      display: 'inline-flex',
      transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 120ms ease',
      flexShrink: 0,
    }}
  >
    <ChevronRightIcon width={14} height={14} />
  </Box>
);

// PHA-678 review fix (addi feedback #3): the per-patient actions used to sit on
// the collapsed header — it made no sense to open a chart before seeing the
// readings. The toolbar now renders only inside an expanded group, as a band
// above the item rows. Acknowledging is handled globally by the selection bar
// at the top of the list (it can span multiple patients), so the only
// per-patient action left here is Open Chart. Shared by PatientGroup and
// PatientSubmissionsGroup.
const GroupToolbar: React.FC<{
  onOpenChart: (e: React.MouseEvent) => void;
}> = ({ onOpenChart }) => (
  <Flex
    align="center"
    gap="2"
    style={{
      padding: '6px 8px',
      marginBottom: 4,
      backgroundColor: 'var(--gray-a2)',
      border: '1px solid var(--gray-a3)',
      borderRadius: 6,
    }}
  >
    <Button size="1" variant="soft" color="gray" onClick={onOpenChart}>
      Open Chart
    </Button>
  </Flex>
);

const Row: React.FC<{
  item: SmartHubItem;
  onSelect: (item: SmartHubItem) => void;
  isChecked?: boolean;
  onToggle?: () => void;
}> = ({
  item,
  onSelect,
  isChecked,
  onToggle,
}) => {
  const { primary, secondary } = itemSummary(item);
  const isOverdue = isTask(item) && item.is_overdue;
  // PHA-510: ehr_change items short-circuit the consumer's onSelect (which
  // is wired up to AckDrawer / vital-sign flow that doesn't understand the
  // shape) and instead run the dedicated auto-ack-and-navigate flow.
  const handleClick = () => {
    if (isEhrChange(item)) {
      // PHA-1141 / PHA-1142: itemised changes are reviewed in the chart, not acknowledged here.
      const section = firstSection(ehrChangeLines(item));
      if (section) {
        openEhrChangeInChart(item, section);
        return;
      }
      void smartHubApi.acknowledgeEhrChange({
        ehr_sync_run_id: item.ehr_sync_run_id,
        dwell_seconds: 0,
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('PHA-510: ack of ehr_sync_runs failed', e);
      });
      window.dispatchEvent(
        new CustomEvent('phanova:open-patient-detail', {
          detail: {
            patientId: item.patient_id,
            initialTab: PATIENT_DETAIL_CARE_PLANS_TAB,
          },
        }),
      );
      return;
    }
    onSelect(item);
  };
  // PHA-678 review fix (addi feedback #4): the checkbox tap target was a tiny
  // size-1 box, so taps often landed on the row and opened the reading instead.
  // The row is now split into two zones with clear separation: a wide, full-
  // height toggle column on the left (clicking anywhere in it checks the item)
  // and the content area on the right (clicking it opens the reading).
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 6,
        border: '1px solid var(--gray-a4)',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {onToggle && (
        <Flex
          align="center"
          justify="center"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          style={{
            minWidth: 40,
            alignSelf: 'stretch',
            flexShrink: 0,
            cursor: 'pointer',
            borderRight: '1px solid var(--gray-a3)',
            backgroundColor: isChecked ? 'var(--green-a3)' : 'var(--gray-a2)',
            transition: 'background-color 120ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = isChecked
              ? 'var(--green-a4)'
              : 'var(--gray-a4)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = isChecked
              ? 'var(--green-a3)'
              : 'var(--gray-a2)';
          }}
        >
          <Checkbox
            size="2"
            variant="surface"
            color="green"
            checked={isChecked}
            onCheckedChange={() => onToggle()}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: 'pointer' }}
          />
        </Flex>
      )}
      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          padding: '8px 10px',
          transition: 'background-color 120ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--gray-a2)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
        }}
      >
        <Flex justify="between" align="start" gap="2">
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text size="1" color="gray" as="div" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {primary}
            </Text>
            <Text size="1" color="gray" as="div">
              {secondary}
            </Text>
          </Box>
          <Flex direction="column" gap="1" align="end" style={{ flexShrink: 0 }}>
            {isOverdue && (
              <Badge color="orange" variant="soft" size="1">
                Overdue
              </Badge>
            )}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

interface PatientGroupProps {
  sectionTitle: string;
  patientId: string;
  patientName: string;
  items: SmartHubItem[];
  onSelect: (item: SmartHubItem) => void;
  // PHA-678: checkbox support
  checked: Map<string, Set<string>>;
  onToggleItem: (groupKey: string, key: string) => void;
  onToggleAll: (groupKey: string, keys: string[]) => void;
}

const PatientGroup: React.FC<PatientGroupProps> = ({
  sectionTitle,
  patientId,
  patientName,
  items,
  onSelect,
  checked,
  onToggleItem,
  onToggleAll,
}) => {
  const [open, setOpen] = useState(false);
  const groupKey = `${sectionTitle}:${patientId}`;

  // Only ackable items get checkboxes (exclude tasks)
  const ackableItems = useMemo(
    () => items.filter((i) => !isTask(i)),
    [items],
  );
  const ackableKeys = useMemo(
    () => ackableItems.map((i) => itemKey(i)).filter((k): k is string => k !== null),
    [ackableItems],
  );

  const checkedSet = checked.get(groupKey);
  const checkedCount = checkedSet?.size ?? 0;
  const allChecked = ackableKeys.length > 0 && checkedCount === ackableKeys.length;
  const someChecked = checkedCount > 0 && !allChecked;

  const handleOpenChart = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent('phanova:open-patient-detail', {
        detail: { patientId },
      }),
    );
  };

  return (
    <Box style={{ borderLeft: '2px solid var(--gray-a4)', paddingLeft: 8 }}>
      <Flex
        align="center"
        gap="2"
        style={{ padding: '4px 0', userSelect: 'none' }}
      >
        {ackableKeys.length > 0 && (
          <Checkbox
            size="2"
            variant="surface"
            color="green"
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            onCheckedChange={() => onToggleAll(groupKey, ackableKeys)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Flex
          role="button"
          tabIndex={0}
          align="center"
          gap="2"
          style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          <Chevron open={open} />
          <Text
            size="2"
            weight="medium"
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {patientName || patientId}
          </Text>
          <Badge size="1" variant="soft" color="gray">
            {items.length}
          </Badge>
        </Flex>
      </Flex>
      {open && (
        <Flex direction="column" gap="1" mt="1" mb="1">
          <GroupToolbar onOpenChart={handleOpenChart} />
          {items.map((item, idx) => {
            const key = itemKey(item);
            return (
              <Row
                key={`${patientId}-${idx}`}
                item={item}
                onSelect={onSelect}
                isChecked={key !== null && (checkedSet?.has(key) ?? false)}
                onToggle={key !== null ? () => onToggleItem(groupKey, key) : undefined}
              />
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

interface CollapsibleSectionProps {
  title: string;
  items: SmartHubItem[];
  emptyHint?: string;
  onSelect: (item: SmartHubItem) => void;
  countOverride?: number;
  // PHA-678
  checked: Map<string, Set<string>>;
  onToggleItem: (groupKey: string, key: string) => void;
  onToggleAll: (groupKey: string, keys: string[]) => void;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  items,
  emptyHint,
  onSelect,
  countOverride,
  checked,
  onToggleItem,
  onToggleAll,
}) => {
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<string, { patientName: string; items: SmartHubItem[] }>();
    for (const item of items) {
      const existing = map.get(item.patient_id);
      if (existing) {
        existing.items.push(item);
      } else {
        map.set(item.patient_id, { patientName: item.patient_name, items: [item] });
      }
    }
    return Array.from(map.entries()).map(([pid, g]) => ({
      patientId: pid,
      ...g,
    }));
  }, [items]);

  return (
    <Box>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
      >
        <Chevron open={open} />
        <Text size="2" weight="bold" style={{ flex: 1 }}>
          {title}
        </Text>
        <Badge variant="soft" color="gray" size="1">
          {countOverride ?? items.length}
        </Badge>
      </Flex>
      {open && (
        <Box mt="2">
          {items.length === 0 ? (
            <Text size="1" color="gray" as="div">
              {emptyHint ?? 'Nothing right now.'}
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {groups.map((g) => (
                <PatientGroup
                  key={g.patientId}
                  sectionTitle={title}
                  patientId={g.patientId}
                  patientName={g.patientName}
                  items={g.items}
                  onSelect={onSelect}
                  checked={checked}
                  onToggleItem={onToggleItem}
                  onToggleAll={onToggleAll}
                />
              ))}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

const BillingRow: React.FC<{ left: string; right: string }> = ({
  left,
  right,
}) => (
  <Flex
    justify="between"
    align="center"
    gap="2"
    style={{
      padding: 8,
      borderRadius: 6,
      border: '1px solid var(--gray-a4)',
      backgroundColor: '#fff',
    }}
  >
    <Text size="2" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {left}
    </Text>
    <Text size="1" weight="medium" color="gray">
      {right}
    </Text>
  </Flex>
);

const BillingSubSection: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
      >
        <Chevron open={open} />
        <Text size="1" color="gray" weight="medium" style={{ flex: 1 }}>
          {title}
        </Text>
        <Badge size="1" variant="soft" color="gray">
          {count}
        </Badge>
      </Flex>
      {open && <Box mt="1">{children}</Box>}
    </Box>
  );
};

const BillingSection: React.FC<{
  feed: BillingGapsFeed;
  enabledPrograms: Set<string>;
}> = ({ feed, enabledPrograms }) => {
  const [open, setOpen] = useState(false);
  const { ccm_gap_to_20, apcm_no_activity_this_month, rpm_days_short } = feed;
  const rpm_minutes_to_10 = feed.rpm_minutes_to_10 ?? [];
  // PHA-906: a program the clinic doesn't bill shouldn't render its sub-section at
  // all (not even at 0). The backend already empties the rows; gate the header on
  // enabledPrograms, NOT on count — a supported program with transiently zero gaps
  // must still show "0". 'ALL'/unset resolves to all three, so nothing is hidden.
  const showCcm = enabledPrograms.has('CCM');
  const showApcm = enabledPrograms.has('APCM');
  const showRpm = enabledPrograms.has('RPM');
  const total =
    (showCcm ? ccm_gap_to_20.length : 0) +
    (showApcm ? apcm_no_activity_this_month.length : 0) +
    (showRpm ? rpm_days_short.length + rpm_minutes_to_10.length : 0);

  return (
    <Box>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
      >
        <Chevron open={open} />
        <Text size="2" weight="bold" style={{ flex: 1 }}>
          Billing opportunities
        </Text>
        <Badge variant="soft" color="gray" size="1">
          {total}
        </Badge>
      </Flex>
      {open && (
        <Box mt="2">
          {total === 0 ? (
            <Text size="1" color="gray" as="div">
              No outstanding gaps this month.
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {showCcm && (
              <BillingSubSection title="CCM minutes-to-20" count={ccm_gap_to_20.length}>
                <Flex direction="column" gap="1">
                  {ccm_gap_to_20.slice(0, 50).map((row) => (
                    <BillingRow
                      key={row.patient_id}
                      left={row.patient_name || row.patient_id}
                      right={`${row.spent_minutes.toFixed(0)} / ${row.target_minutes ?? 20} min · gap ${row.gap_minutes.toFixed(0)}`}

                    />
                  ))}
                </Flex>
              </BillingSubSection>
              )}
              {showApcm && (
              <BillingSubSection title="APCM no activity" count={apcm_no_activity_this_month.length}>
                <Flex direction="column" gap="1">
                  {apcm_no_activity_this_month.slice(0, 50).map((row) => (
                    <BillingRow
                      key={row.patient_id}
                      left={row.patient_name || row.patient_id}
                      right={row.apcm_tier ? `Tier ${row.apcm_tier}` : 'APCM'}

                    />
                  ))}
                </Flex>
              </BillingSubSection>
              )}
              {showRpm && (
              <BillingSubSection title="RPM minutes-to-10" count={rpm_minutes_to_10.length}>
                <Flex direction="column" gap="1">
                  {rpm_minutes_to_10.slice(0, 50).map((row) => (
                    <BillingRow
                      key={row.patient_id}
                      left={row.patient_name || row.patient_id}
                      right={`${row.spent_minutes.toFixed(0)} / ${row.target_minutes ?? 10} min · gap ${row.gap_minutes.toFixed(0)}`}
                    />
                  ))}
                </Flex>
              </BillingSubSection>
              )}
              {showRpm && (
              <BillingSubSection title="RPM days short" count={rpm_days_short.length}>
                <Flex direction="column" gap="1">
                  {rpm_days_short.slice(0, 50).map((row) => (
                    <BillingRow
                      key={row.patient_id}
                      left={row.patient_name || row.patient_id}
                      right={`${row.days_with_measurements} / ${row.required_days} days`}

                    />
                  ))}
                </Flex>
              </BillingSubSection>
              )}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

// PHA-510: "EHR changes pending review" bucket. Server emits one row per
// patient with unacked EHR-driven changes; we render them flat (no patient
// grouping) since each row already represents one patient. Click flow: fire
// the ack endpoint, then dispatch `phanova:open-patient-detail` with
// `initialTab: 2` (Care plans) so the clinician lands where the new draft
// lives. Same path for `no_changes_applicable` rows — there's no draft to
// approve but the chart is still where the clinician confirms what changed.
const PATIENT_DETAIL_CARE_PLANS_TAB = 2;

const ehrChangeRowText = (row: SmartHubEhrChangeRow): { primary: string; secondary: string } => {
  // PHA-1142: "No change should be mentioned without a reference to the change itself" — name
  // the changes, with their dates, rather than counting them.
  const lines = ehrChangeLines(row);
  if (lines.length > 0) {
    const { shown, hidden } = fitSummaryLines(lines, 2);
    return {
      primary: row.patient_name || row.patient_id,
      secondary: `${shown.join('; ')}${hidden > 0 ? ` (+${hidden} more)` : ''}`,
    };
  }
  const cs = row.change_summary || {};
  const parts: string[] = [];
  const push = (n: number, singular: string, plural: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? singular : plural}`);
  };
  push((cs.problems_added || []).length, 'new problem', 'new problems');
  push((cs.problems_resolved || []).length, 'problem resolved', 'problems resolved');
  push((cs.meds_added || []).length, 'new medication', 'new medications');
  push((cs.meds_changed || []).length, 'med discontinued', 'meds discontinued');
  // PHA-510 follow-up: vitals + labs flow through the same bucket. Singular
  // copy is "new vital" / "new lab" so a row reading "1 new BP, 1 new A1c"
  // collapses to "1 new vital, 1 new lab". AckDrawer surfaces the full
  // breakdown for inspection.
  push((cs.vitals_added || []).length, 'new vital', 'new vitals');
  push((cs.labs_added || []).length, 'new lab', 'new labs');
  // 'no_changes_applicable' rows reach here with all-zero summaries; the FE
  // still needs a recognizable line so the clinician acknowledges them.
  const summaryText = parts.length > 0 ? parts.join(', ') : 'EHR updated — no plan change needed';
  const since = row.since_date ? ` since ${row.since_date}` : '';
  return {
    primary: row.patient_name || row.patient_id,
    secondary: `${summaryText}${since}`,
  };
};

interface EhrChangeRowProps {
  row: SmartHubEhrChangeRow;
  onAcknowledged?: () => void;
}

const ehrActionButton = (label: string, onClick: () => void, variant: 'soft' | 'outline' = 'soft') => (
  <Button
    size="1"
    variant={variant}
    color={variant === 'soft' ? 'amber' : 'gray'}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {label}
  </Button>
);

const EhrChangeRow: React.FC<EhrChangeRowProps> = ({ row, onAcknowledged }) => {
  const lines = ehrChangeLines(row);
  if (lines.length > 0) {
    return <ItemizedEhrChangeRow row={row} lines={lines} />;
  }
  return <LegacyEhrChangeRow row={row} onAcknowledged={onAcknowledged} />;
};

/**
 * PHA-1141 / PHA-1142: a run with itemised changes. Each change is listed with its date
 * (medications collapse to "name · date" when the list is long), with buttons per kind:
 *   * problems   — Acknowledge (Problems tab, new items highlighted) + Re-generate care plan
 *   * medications — Review medications (Medications tab); no Re-generate, per PHA-1141
 *   * vitals / labs — Acknowledge (Care plans tab, the goal's vitals table) + Re-generate
 * Nothing is acknowledged from here.
 */
const ItemizedEhrChangeRow: React.FC<{ row: SmartHubEhrChangeRow; lines: EhrChangeSummaryLine[] }> = ({
  row,
  lines,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = expanded ? { shown: lines.map((l) => l.text), hidden: 0 } : fitSummaryLines(lines, 4);
  const has = (s: EhrChangeFocusSection) => lines.some((l) => l.section === s);
  const canRegenerate = has('problems') || has('vitals');
  return (
    <Box
      style={{
        padding: '8px 10px',
        borderRadius: 6,
        border: '1px solid var(--gray-a4)',
        backgroundColor: '#fff',
      }}
    >
      <Text size="2" weight="medium" as="div" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.patient_name || row.patient_id}
      </Text>
      <Box style={{ margin: '4px 0 6px' }}>
        {shown.map((text, i) => (
          <Text key={i} size="1" color="gray" as="div">
            {text}
          </Text>
        ))}
        {(hidden > 0 || expanded) && (
          <Text
            size="1"
            color="blue"
            as="div"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => setExpanded((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((v) => !v);
              }
            }}
          >
            {expanded ? 'Show less' : `+${hidden} more`}
          </Text>
        )}
      </Box>
      <Flex gap="2" wrap="wrap">
        {has('problems') && ehrActionButton('Acknowledge problems', () => openEhrChangeInChart(row, 'problems'))}
        {has('medications') && ehrActionButton('Review medications', () => openEhrChangeInChart(row, 'medications'))}
        {has('vitals') && ehrActionButton('Acknowledge vitals', () => openEhrChangeInChart(row, 'vitals'))}
        {canRegenerate && ehrActionButton('Re-generate care plan', () => startEhrChangeRegeneration(row), 'outline')}
      </Flex>
    </Box>
  );
};

/** A run with nothing itemised ("EHR updated — no plan change needed"): click to acknowledge. */
const LegacyEhrChangeRow: React.FC<EhrChangeRowProps> = ({ row, onAcknowledged }) => {
  const { primary, secondary } = ehrChangeRowText(row);
  const hasDraft = row.status === 'changes_found' && row.draft_care_plan_id !== null;
  const onClick = async () => {
    // Auto-fire the ack so the row disappears on next poll. Best-effort:
    // navigation should still happen even if the ack call fails (clinician
    // can dismiss manually from Smart Hub later).
    try {
      await smartHubApi.acknowledgeEhrChange({
        ehr_sync_run_id: row.ehr_sync_run_id,
        dwell_seconds: 0,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('PHA-510: ack of ehr_sync_runs failed', e);
    }
    onAcknowledged?.();
    window.dispatchEvent(
      new CustomEvent('phanova:open-patient-detail', {
        detail: {
          patientId: row.patient_id,
          initialTab: PATIENT_DETAIL_CARE_PLANS_TAB,
        },
      }),
    );
  };
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        cursor: 'pointer',
        padding: '8px 10px',
        borderRadius: 6,
        border: '1px solid var(--gray-a4)',
        backgroundColor: '#fff',
        transition: 'background-color 120ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--gray-a2)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
      }}
    >
      <Flex justify="between" align="start" gap="2">
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text size="2" weight="medium" as="div" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {primary}
          </Text>
          <Text size="1" color="gray" as="div">
            {secondary}
          </Text>
        </Box>
        {/* `no_changes_applicable` rows render in muted gray — clinician acks
            but there's no draft to approve. `changes_found` rows render with
            an amber chip to signal "draft awaiting review". */}
        {hasDraft ? (
          <Badge color="amber" variant="soft" size="1">
            Review
          </Badge>
        ) : (
          <Badge color="gray" variant="soft" size="1">
            Acknowledge
          </Badge>
        )}
      </Flex>
    </Box>
  );
};

interface EhrChangesSectionProps {
  items: SmartHubEhrChangeRow[];
  onAcknowledged?: () => void;
}

const EhrChangesSection: React.FC<EhrChangesSectionProps> = ({ items, onAcknowledged }) => {
  const [open, setOpen] = useState(items.length > 0);   // open by default when there's work
  if (items.length === 0) {
    // Hide entirely when empty — Zack-style copy ("All EHR-driven changes
    // reviewed") is deferred to PHA-523's @tom + @addi alignment.
    return null;
  }
  return (
    <Box>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
      >
        <Chevron open={open} />
        <Text size="2" weight="bold" style={{ flex: 1 }}>
          EHR changes pending review
        </Text>
        <Badge variant="soft" color="amber" size="1">
          {items.length}
        </Badge>
      </Flex>
      {open && (
        <Box mt="2">
          <Flex direction="column" gap="2">
            {items.map((row) => (
              <EhrChangeRow
                key={row.ehr_sync_run_id}
                row={row}
                onAcknowledged={onAcknowledged}
              />
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

// PHA-479: Recent patient answers — grouped by submission_id so one survey
// response counts as 1, regardless of how many answers it carries. PHA-481:
// submissions are then nested under a per-patient row so a patient with
// multiple submissions appears once at the top level (`SubmissionGroup` is
// rendered with `hidePatientName` since the patient row above already
// identifies them).
interface SubmissionGroupProps {
  group: SmartHubSubmissionGroup;
  onSelect: (item: SmartHubItem) => void;
  onSelectSubmission: (group: SmartHubSubmissionGroup) => void;
  hidePatientName?: boolean;
  // PHA-678: checkbox support (passed through from PatientSubmissionsGroup)
  groupKey?: string;
  checkedSet?: Set<string>;
  onToggleItem?: (groupKey: string, key: string) => void;
}

const SubmissionGroup: React.FC<SubmissionGroupProps> = ({
  group,
  onSelect,
  onSelectSubmission,
  hidePatientName,
  groupKey,
  checkedSet,
  onToggleItem,
}) => {
  const [open, setOpen] = useState(false);
  const { patientId, patientName, answers, submission_time } = {
    patientId: group.patient_id,
    patientName: group.patient_name,
    answers: group.answers,
    submission_time: group.submission_time,
  };
  return (
    <Box style={{ borderLeft: '2px solid var(--gray-a4)', paddingLeft: 8 }}>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', padding: '4px 0', userSelect: 'none' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <Chevron open={open} />
        {hidePatientName ? (
          <Text
            size="2"
            color="gray"
            style={{ flex: 1, minWidth: 0 }}
          >
            {formatRelative(submission_time) || 'Submission'}
          </Text>
        ) : (
          <Text
            size="2"
            weight="medium"
            title="Open patient details"
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              color: 'var(--accent-11)',
            }}
            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
              e.stopPropagation();
              window.dispatchEvent(
                new CustomEvent('phanova:open-patient-detail', {
                  detail: { patientId },
                }),
              );
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLSpanElement>) => {
              (e.currentTarget as HTMLElement).style.textDecoration = 'underline';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLSpanElement>) => {
              (e.currentTarget as HTMLElement).style.textDecoration = 'none';
            }}
          >
            {patientName || patientId}
          </Text>
        )}
        {!hidePatientName && (
          <Text size="1" color="gray" style={{ flexShrink: 0 }}>
            {formatRelative(submission_time)}
          </Text>
        )}
        <Badge size="1" variant="soft" color="gray">
          {answers.length}
        </Badge>
      </Flex>
      {open && (
        <Flex direction="column" gap="1" mt="1" mb="1">
          <Box>
            <Button
              size="1"
              variant="soft"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                onSelectSubmission(group);
              }}
            >
              Acknowledge all ({answers.length})
            </Button>
          </Box>
          {answers.map((a, idx) => {
            const key = itemKey(a);
            return (
              <Row
                key={`${group.submission_id}-${idx}`}
                item={a}
                onSelect={onSelect}
                isChecked={key !== null && (checkedSet?.has(key) ?? false)}
                onToggle={key !== null && groupKey && onToggleItem
                  ? () => onToggleItem(groupKey, key)
                  : undefined}
              />
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

// PHA-481: outer per-patient wrapper so a patient with N submissions appears
// as one row (not N rows). The patient-link affordance lives here; nested
// `SubmissionGroup`s render with `hidePatientName`.
interface PatientSubmissionsGroupProps {
  patientId: string;
  patientName: string;
  submissions: SmartHubSubmissionGroup[];
  onSelect: (item: SmartHubItem) => void;
  onSelectSubmission: (group: SmartHubSubmissionGroup) => void;
  // PHA-678
  checked: Map<string, Set<string>>;
  onToggleItem: (groupKey: string, key: string) => void;
  onToggleAll: (groupKey: string, keys: string[]) => void;
}

const PatientSubmissionsGroup: React.FC<PatientSubmissionsGroupProps> = ({
  patientId,
  patientName,
  submissions,
  onSelect,
  onSelectSubmission,
  checked,
  onToggleItem,
  onToggleAll,
}) => {
  const [open, setOpen] = useState(false);
  const groupKey = `Recent patient answers:${patientId}`;

  // Gather all answer items across all submissions for this patient
  const allAnswerItems = useMemo(
    () => submissions.flatMap((sub) => sub.answers as SmartHubItem[]),
    [submissions],
  );
  const ackableKeys = useMemo(
    () => allAnswerItems.map((i) => itemKey(i)).filter((k): k is string => k !== null),
    [allAnswerItems],
  );

  const checkedSet = checked.get(groupKey);
  const checkedCount = checkedSet?.size ?? 0;
  const allChecked = ackableKeys.length > 0 && checkedCount === ackableKeys.length;
  const someChecked = checkedCount > 0 && !allChecked;

  const handleOpenChart = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent('phanova:open-patient-detail', {
        detail: { patientId },
      }),
    );
  };

  return (
    <Box style={{ borderLeft: '2px solid var(--gray-a4)', paddingLeft: 8 }}>
      <Flex
        align="center"
        gap="2"
        style={{ padding: '4px 0', userSelect: 'none' }}
      >
        {ackableKeys.length > 0 && (
          <Checkbox
            size="2"
            variant="surface"
            color="green"
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            onCheckedChange={() => onToggleAll(groupKey, ackableKeys)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Flex
          role="button"
          tabIndex={0}
          align="center"
          gap="2"
          style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          <Chevron open={open} />
          <Text
            size="2"
            weight="medium"
            title="Open patient details"
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              color: 'var(--accent-11)',
            }}
            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
              e.stopPropagation();
              window.dispatchEvent(
                new CustomEvent('phanova:open-patient-detail', {
                  detail: { patientId },
                }),
              );
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLSpanElement>) => {
              (e.currentTarget as HTMLElement).style.textDecoration = 'underline';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLSpanElement>) => {
              (e.currentTarget as HTMLElement).style.textDecoration = 'none';
            }}
          >
            {patientName || patientId}
          </Text>
          <Badge size="1" variant="soft" color="gray">
            {submissions.length}
          </Badge>
        </Flex>
      </Flex>
      {open && (
        <Flex direction="column" gap="2" mt="1" mb="1">
          <GroupToolbar onOpenChart={handleOpenChart} />
          {submissions.map((sub) => (
            <SubmissionGroup
              key={sub.submission_id}
              group={sub}
              onSelect={onSelect}
              onSelectSubmission={onSelectSubmission}
              hidePatientName
              groupKey={groupKey}
              checkedSet={checkedSet}
              onToggleItem={onToggleItem}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

interface PatientAnswersSectionProps {
  items: SmartHubAnswerRow[];
  onSelect: (item: SmartHubItem) => void;
  onSelectSubmission: (group: SmartHubSubmissionGroup) => void;
  // PHA-678
  checked: Map<string, Set<string>>;
  onToggleItem: (groupKey: string, key: string) => void;
  onToggleAll: (groupKey: string, keys: string[]) => void;
}

const PatientAnswersSection: React.FC<PatientAnswersSectionProps> = ({
  items,
  onSelect,
  onSelectSubmission,
  checked,
  onToggleItem,
  onToggleAll,
}) => {
  const [open, setOpen] = useState(false);

  // PHA-481: group by patient first, then by submission inside each patient.
  // The section badge counts submissions (drives the To-do invariant); the
  // patient-row badge counts that patient's submissions.
  const patientGroups = useMemo(() => {
    const submissionMap = new Map<string, SmartHubSubmissionGroup>();
    for (const a of items) {
      const existing = submissionMap.get(a.submission_id);
      if (existing) {
        existing.answers.push(a);
      } else {
        submissionMap.set(a.submission_id, {
          source_kind: 'submission',
          submission_id: a.submission_id,
          patient_id: a.patient_id,
          patient_name: a.patient_name,
          submission_time: a.submission_time,
          answers: [a],
        });
      }
    }
    const byPatient = new Map<
      string,
      { patientName: string; submissions: SmartHubSubmissionGroup[] }
    >();
    for (const sub of Array.from(submissionMap.values())) {
      const existing = byPatient.get(sub.patient_id);
      if (existing) {
        existing.submissions.push(sub);
      } else {
        byPatient.set(sub.patient_id, {
          patientName: sub.patient_name,
          submissions: [sub],
        });
      }
    }
    return Array.from(byPatient.entries()).map(([pid, g]) => ({
      patientId: pid,
      patientName: g.patientName,
      submissions: g.submissions,
    }));
  }, [items]);

  const totalSubmissions = patientGroups.reduce(
    (acc, pg) => acc + pg.submissions.length,
    0,
  );

  return (
    <Box>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap="2"
        style={{ cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
      >
        <Chevron open={open} />
        <Text size="2" weight="bold" style={{ flex: 1 }}>
          Recent patient answers
        </Text>
        <Badge variant="soft" color="gray" size="1">
          {totalSubmissions}
        </Badge>
      </Flex>
      {open && (
        <Box mt="2">
          {patientGroups.length === 0 ? (
            <Text size="1" color="gray" as="div">
              No new patient answers.
            </Text>
          ) : (
            <Flex direction="column" gap="2">
              {patientGroups.map((pg) => (
                <PatientSubmissionsGroup
                  key={pg.patientId}
                  patientId={pg.patientId}
                  patientName={pg.patientName}
                  submissions={pg.submissions}
                  onSelect={onSelect}
                  onSelectSubmission={onSelectSubmission}
                  checked={checked}
                  onToggleItem={onToggleItem}
                  onToggleAll={onToggleAll}
                />
              ))}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

interface TaskBarProps {
  feed: TaskBarFeed | null;
  billing: BillingGapsFeed | null;
  billingDenied: boolean;
  // PHA-906: programs the selected clinic bills; a billing sub-section for an
  // unsupported program is hidden entirely. 'ALL'/unset -> all three.
  enabledPrograms: Set<string>;
  loading: boolean;
  onSelect: (item: SmartHubItem) => void;
  onSelectSubmission: (group: SmartHubSubmissionGroup) => void;
  // PHA-510: optional callback fired after a successful ehr_change ack so
  // the consumer (Dashboard) can refetch the task bar feed and the row
  // disappears immediately rather than waiting for the next 60 s poll.
  onEhrChangeAcknowledged?: () => void;
  // PHA-678: bulk acknowledge the current selection, grouped per patient.
  // The global selection bar collects every checked item across all sections
  // and hands back one BulkAckGroup per patient.
  onBulkAcknowledge?: (groups: BulkAckGroup[]) => void;
}

const TaskBar: React.FC<TaskBarProps> = ({
  feed,
  billing,
  billingDenied,
  enabledPrograms,
  loading,
  onSelect,
  onSelectSubmission,
  onEhrChangeAcknowledged,
  onBulkAcknowledge,
}) => {
  // PHA-678: checkbox state — Map<groupKey, Set<itemKey>>
  // groupKey = "${sectionTitle}:${patientId}"
  const [checked, setChecked] = useState<Map<string, Set<string>>>(new Map());

  const toggleItem = useCallback((groupKey: string, key: string) => {
    setChecked((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(groupKey) || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      if (set.size === 0) next.delete(groupKey);
      else next.set(groupKey, set);
      return next;
    });
  }, []);

  const toggleAll = useCallback((groupKey: string, keys: string[]) => {
    setChecked((prev) => {
      const next = new Map(prev);
      const existing = next.get(groupKey);
      const allChecked = existing?.size === keys.length;
      if (allChecked) {
        next.delete(groupKey);
      } else {
        next.set(groupKey, new Set(keys));
      }
      return next;
    });
  }, []);

  // PHA-678 review fix (addi feedback #2): the 60s poll replaces the feed
  // object every cycle. Wiping all checks on any feed change cleared the
  // user's in-progress selections mid-task. Instead, PRUNE only the keys for
  // items that are no longer in the feed (e.g. acked elsewhere), preserving
  // everything the user has selected that is still present.
  useEffect(() => {
    if (!feed) return;
    const present = new Set<string>();
    const collect = (arr?: readonly SmartHubItem[]) => {
      arr?.forEach((i) => {
        const k = itemKey(i);
        if (k) present.add(k);
      });
    };
    collect(feed.out_of_bounds_vital_signs as SmartHubItem[]);
    collect(feed.hospitalizations as SmartHubItem[]);
    collect(feed.new_vital_signs as SmartHubItem[]);
    collect(feed.new_device_readings as SmartHubItem[]);
    collect(feed.new_patient_answers as SmartHubItem[]);
    collect(feed.ehr_changes_pending as SmartHubItem[]);
    collect(feed.todo as SmartHubItem[]);
    setChecked((prev) => {
      let changed = false;
      const next = new Map<string, Set<string>>();
      for (const [groupKey, keys] of prev) {
        const kept = new Set<string>();
        for (const k of keys) {
          if (present.has(k)) kept.add(k);
          else changed = true;
        }
        if (kept.size > 0) next.set(groupKey, kept);
      }
      return changed ? next : prev;
    });
  }, [feed]);
  if (loading && !feed) {
    return (
      <Text size="2" color="gray">
        Loading…
      </Text>
    );
  }
  if (!feed) {
    return (
      <Text size="2" color="gray">
        Smart Hub feed unavailable.
      </Text>
    );
  }
  // PHA-678: resolve the current selection (Map<groupKey, Set<itemKey>>) into
  // per-patient groups for the global selection bar. Build a feed-wide index
  // itemKey -> {item, patientId, patientName}, then walk every checked key,
  // dedupe across groups (the same item can't appear twice), and bucket by
  // patient so acknowledging fires one bulk call (one 60s event) per patient.
  const selectionGroups: BulkAckGroup[] = (() => {
    const index = new Map<
      string,
      { item: SmartHubItem; patientId: string; patientName: string }
    >();
    const indexAll = (arr?: readonly SmartHubItem[]) => {
      arr?.forEach((i) => {
        const k = itemKey(i);
        if (k && !index.has(k)) {
          index.set(k, { item: i, patientId: i.patient_id, patientName: i.patient_name });
        }
      });
    };
    indexAll(feed.out_of_bounds_vital_signs as SmartHubItem[]);
    indexAll(feed.hospitalizations as SmartHubItem[]);
    indexAll(feed.new_vital_signs as SmartHubItem[]);
    indexAll(feed.new_device_readings as SmartHubItem[]);
    indexAll(feed.new_patient_answers as SmartHubItem[]);
    indexAll(feed.ehr_changes_pending as SmartHubItem[]);
    indexAll(feed.todo as SmartHubItem[]);

    const seen = new Set<string>();
    const byPatient = new Map<string, BulkAckGroup>();
    for (const keys of checked.values()) {
      for (const k of keys) {
        if (seen.has(k)) continue;
        const entry = index.get(k);
        if (!entry) continue; // item no longer in feed (defensive)
        seen.add(k);
        const group = byPatient.get(entry.patientId);
        if (group) {
          group.items.push(entry.item);
        } else {
          byPatient.set(entry.patientId, {
            patientId: entry.patientId,
            patientName: entry.patientName,
            items: [entry.item],
          });
        }
      }
    }
    return Array.from(byPatient.values());
  })();
  const selectedCount = selectionGroups.reduce((acc, g) => acc + g.items.length, 0);

  const showBilling = !billingDenied && billing !== null;
  // PHA-906: exclude hidden programs from the To-do badge roll-up too, so the
  // count matches the visible sub-sections. (The backend already empties these
  // for a specific org; gating here keeps the badge correct in every case.)
  const billingTotal = showBilling
    ? (enabledPrograms.has('CCM') ? billing!.ccm_gap_to_20.length : 0) +
      (enabledPrograms.has('APCM') ? billing!.apcm_no_activity_this_month.length : 0) +
      (enabledPrograms.has('RPM')
        ? billing!.rpm_days_short.length + (billing!.rpm_minutes_to_10 ?? []).length
        : 0)
    : 0;
  // PHA-479: count distinct submissions, not distinct answers, for the to-do
  // counter so a 5-question response shows as 1.
  const distinctNewAnswerSubmissions = new Set(
    feed.new_patient_answers.map((a) => a.submission_id),
  ).size;
  // The server still emits one priority-tagged item per answer in `feed.todo`;
  // adjust the counter so the visible count matches the section badges.
  const todoCount =
    feed.todo.length - feed.new_patient_answers.length + distinctNewAnswerSubmissions;
  // Display-only dedup of care-plan tasks. The backend keeps one row per
  // task_occurrence (each is a separate completion unit), so the same task
  // text + due_at can repeat once per disease/care plan that triggered it,
  // OR — as observed in prod — once per stale task_occurrences row that
  // was never cleaned up after a care-plan re-upload. Key on the rendered
  // calendar day rather than the raw due_at ISO so sub-second timestamp
  // jitter on those stale rows can't slip duplicates through. The patient's
  // Care Plans tab still lists each occurrence individually under its
  // disease accordion.
  const dedupedOverdueUpcoming = (() => {
    const seen = new Map<string, SmartHubTaskRow>();
    for (const t of feed.overdue_upcoming_tasks) {
      const dayKey = formatTaskCalendarDay(t.due_at);
      const key = `${t.patient_id}|${t.task}|${dayKey}`;
      const existing = seen.get(key);
      if (existing) {
        existing.duplicate_count = (existing.duplicate_count ?? 1) + 1;
      } else {
        seen.set(key, { ...t, duplicate_count: 1 });
      }
    }
    return Array.from(seen.values());
  })();
  return (
    <Flex direction="column" gap="3">
      {/* PHA-678 (addi feedback): a single selection action bar so items
          selected across any patients/sections are actionable. Sticks to the
          top of the list while you scroll and collect a selection. */}
      {onBulkAcknowledge && selectedCount > 0 && (
        <Flex
          align="center"
          gap="3"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            padding: '8px 12px',
            backgroundColor: 'var(--color-panel-solid)',
            border: '1px solid var(--green-a6)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-3)',
          }}
        >
          <Text size="2" weight="medium" style={{ flex: 1 }}>
            {selectedCount} selected
          </Text>
          <Button
            size="2"
            variant="solid"
            color="green"
            onClick={() => onBulkAcknowledge(selectionGroups)}
          >
            Acknowledge ({selectedCount})
          </Button>
          <Button
            size="2"
            variant="soft"
            color="gray"
            onClick={() => setChecked(new Map())}
          >
            Clear
          </Button>
        </Flex>
      )}
      {/* PHA-510: above overdue_upcoming_tasks per parent issue (highest
          clinical urgency). Hidden when the bucket is empty. */}
      <EhrChangesSection
        items={feed.ehr_changes_pending}
        onAcknowledged={onEhrChangeAcknowledged}
      />
      <CollapsibleSection
        title="Hospitalizations"
        items={feed.hospitalizations}
        emptyHint="No unacknowledged hospitalization answers."
        onSelect={onSelect}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      <CollapsibleSection
        title="Recent abnormal vital signs"
        items={feed.out_of_bounds_vital_signs}
        emptyHint="No out-of-range BP or blood-sugar readings."
        onSelect={onSelect}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      <CollapsibleSection
        title="Recent vital signs"
        items={feed.new_vital_signs}
        emptyHint="No new in-range BP or blood-sugar readings."
        onSelect={onSelect}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      <CollapsibleSection
        title="Recent device readings"
        items={feed.new_device_readings}
        emptyHint="No new RPM measurements."
        onSelect={onSelect}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      <PatientAnswersSection
        items={feed.new_patient_answers}
        onSelect={onSelect}
        onSelectSubmission={onSelectSubmission}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      <CollapsibleSection
        title="Care-plan tasks"
        items={dedupedOverdueUpcoming}
        emptyHint="No overdue or upcoming tasks."
        onSelect={onSelect}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
      {showBilling && (
        <BillingSection feed={billing!} enabledPrograms={enabledPrograms} />
      )}
      <Separator size="4" />
      <CollapsibleSection
        title="To-do"
        items={feed.todo}
        emptyHint="Nothing prioritized right now."
        onSelect={onSelect}
        countOverride={todoCount + billingTotal}
        checked={checked}
        onToggleItem={toggleItem}
        onToggleAll={toggleAll}
      />
    </Flex>
  );
};

export default TaskBar;
